// `/invitations/accept?token=` (coverage matrix row 11, P0): where the invitation email lands
// (services/api/src/domain/org/invitations.ts `inviteLink`). A public route, outside the auth
// guard, because the invitee usually has no session and no password yet:
// - signed out: "Email me a sign-in link" (Better Auth magic link). The link comes back here
//   with `continue=1`, signed in with a verified email, and the invitation is accepted at once;
// - signed in: `POST /v1/invitations/accept` on "Accept invitation" (or at once after a link),
//   then `/v1/me` is read again and the user lands in the company (`/c/{slug}`);
// - refused: 404 `invitation_invalid` (expired, superseded, used, or another address), 403
//   `email_unverified` or `forbidden`, each with what to do next. ONBOARD: the API's `reason`
//   tells expired, withdrawn (revoked or replaced), used and another address apart, and a
//   member of the invitation's company gets "Go to {company}" instead of a dead end.
// - GUIDED-FLOW: once accepted, a seat setup sent with the invitation (an open flow of the new
//   member's) opens as its full-screen panel instead of the company's landing.
// The session is read with a plain request, not the shared `["me"]` query: a 401 here is the
// normal signed-out state, not an expired session (api/session-expiry.ts skips this path).
import { ApiError } from "@companyos/sdk";
import { Button, buttonVariants, EmptyState, Notice, Spinner } from "@companyos/ui";
import { useQueryClient } from "@tanstack/react-query";
import { createRoute, Link, useRouter } from "@tanstack/react-router";
import { CircleAlertIcon, LinkIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { meQuery } from "../api/query";
import type { MeResponse } from "../api/types";
import { AuthFrame, MagicLinkForm } from "../features/auth/auth-frame";
import { rootRoute } from "./__root";

type AcceptSearch = { token?: string; continue?: boolean };

/** `{company id}.{invitation id}.{43 base64url chars}` (the API refuses anything else). */
const TOKEN = /^[0-9a-f-]{36}\.[0-9a-f-]{36}\.[A-Za-z0-9_-]{43}$/i;

export const invitationAcceptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "invitations/accept",
  validateSearch: (search: Record<string, unknown>): AcceptSearch => ({
    ...(typeof search.token === "string" ? { token: search.token } : {}),
    ...(search.continue === true || search.continue === "1" || search.continue === 1
      ? { continue: true }
      : {}),
  }),
  component: AcceptInvitationRoute,
});

/** One page state per token: another invitation opened in place starts clean (ONBOARD). */
function AcceptInvitationRoute() {
  const { token } = invitationAcceptRoute.useSearch();
  return <AcceptInvitationPage key={token ?? ""} />;
}

type Session = { state: "loading" } | { state: "out" } | { state: "in"; me: MeResponse };
type Refusal = {
  title: string;
  body: string;
  relink: boolean;
  /** ONBOARD: already a member of the invitation's company: a way in instead of a dead end. */
  open?: { slug: string; name: string };
};

/** The company a well-formed token names (its first segment). */
const companyOfToken = (token: string) => token.split(".")[0]?.toLowerCase() ?? "";

function refusalOf(error: unknown, me: MeResponse | null, token: string): Refusal {
  const email = me?.user.email ?? "";
  const code = error instanceof ApiError ? error.code : null;
  if (code === "invitation_invalid") {
    // ONBOARD: the API says why once the token matched its invitation (`reason`).
    const problem = (error as ApiError).problem;
    const reason = problem?.code === "invitation_invalid" ? problem.reason : undefined;
    const joined = me?.memberships.find((m) => m.company_id === companyOfToken(token));
    // Already a member (typically: the link opened again after joining): no dead end. A link
    // meant for someone else still says so, even to a member.
    if (joined && reason !== "other_address")
      return {
        title: `You're already in ${joined.company_name}`,
        body: "Your account is a member of this company, so the invitation isn't needed. Go straight in.",
        relink: false,
        open: { slug: joined.company_slug, name: joined.company_name },
      };
    if (reason === "accepted")
      return {
        title: "This invitation has already been used",
        body: "Each invitation works once. If you accepted it with another account, sign in with that one. Otherwise ask the person who invited you for a new invitation.",
        relink: false,
      };
    if (reason === "revoked")
      return {
        title: "This invitation was withdrawn",
        body: "It was revoked, or replaced by a newer invitation to the same address. Open the newest invitation email, or ask the person who invited you to send one again.",
        relink: false,
      };
    if (reason === "expired")
      return {
        title: "This invitation has expired",
        body: "Invitations work for 14 days. Ask the person who invited you to send a new one.",
        relink: false,
      };
    if (reason === "other_address")
      return {
        title: "This invitation is for another email address",
        body: `It was sent to an address other than ${email}. Sign in with the address the invitation was sent to.`,
        relink: false,
      };
    return {
      title: "This invitation can't be used",
      body: `It may have expired, been replaced by a newer invitation, been used already, or been sent to an address other than ${email}. Sign in with the address it was sent to, or ask the person who invited you for a new one.`,
      relink: false,
    };
  }
  if (code === "email_unverified")
    return {
      title: "Confirm your email first",
      body: `${email} has not been confirmed. Send yourself a sign-in link: opening it confirms the address and accepts the invitation.`,
      relink: true,
    };
  if (error instanceof ApiError && error.status === 403)
    return {
      title: "This account can't accept the invitation",
      body: error.problem?.detail ?? "Your account is not allowed to take this role.",
      relink: false,
    };
  return {
    title: "The invitation could not be accepted",
    body: error instanceof Error ? error.message : "Try again in a moment.",
    relink: false,
  };
}

function AcceptInvitationPage() {
  const { api } = invitationAcceptRoute.useRouteContext();
  const search = invitationAcceptRoute.useSearch();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session>({ state: "loading" });
  const [accepting, setAccepting] = useState(false);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const autoTried = useRef(false);
  const token = search.token ?? "";
  const wellFormed = TOKEN.test(token);
  const here = `/invitations/accept?token=${encodeURIComponent(token)}`;

  const readSession = useCallback(async () => {
    try {
      const me = (await api.client.me.get()) as MeResponse;
      setSession({ state: "in", me });
    } catch (e) {
      setSession({ state: "out" });
      if (!(e instanceof ApiError && e.status === 401))
        setRefusal({
          title: "Can't reach CompanyOS",
          body: "Your session could not be checked. Check your connection and reload the page.",
          relink: false,
        });
    }
  }, [api]);

  useEffect(() => {
    void readSession();
  }, [readSession]);

  // Results that land after another token replaced this page are dropped (no stale navigate).
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const accept = useCallback(
    async (me: MeResponse) => {
      setAccepting(true);
      setRefusal(null);
      try {
        const accepted = await api.client.invitations.accept({ body: { token } });
        // The new membership arrives on `/v1/me`; the shell reads it from the shared query.
        queryClient.removeQueries({ queryKey: ["me"] });
        const fresh = await queryClient.fetchQuery(meQuery(api));
        await router.invalidate();
        if (!alive.current) return;
        const slug = fresh.memberships.find(
          (m) => m.company_id === accepted.company_id,
        )?.company_slug;
        // GUIDED-FLOW: a seat setup sent with the invitation opens at once (their part to fill).
        const flows = slug
          ? await api.client.flows
              .list({
                params: { co: accepted.company_id },
                query: { mine: "true", status: "open" },
              })
              .catch(() => null)
          : null;
        if (!alive.current) return;
        const run = flows?.data[0];
        await router.navigate(
          slug && run
            ? { to: "/c/$slug/flows/$id", params: { slug, id: run.id } }
            : slug
              ? { to: "/c/$slug", params: { slug } }
              : { to: "/" },
        );
      } catch (e) {
        if (!alive.current) return;
        setRefusal(refusalOf(e, me, token));
        setAccepting(false);
      }
    },
    [api, queryClient, router, token],
  );

  // Back from the email link: signed in with the address just proven, so accept at once.
  useEffect(() => {
    if (session.state !== "in" || !search.continue || !wellFormed || autoTried.current) return;
    autoTried.current = true;
    void accept(session.me);
  }, [session, search.continue, wellFormed, accept]);

  async function signOut() {
    await api.auth.signOut().catch(() => undefined);
    queryClient.clear();
    setRefusal(null);
    setSession({ state: "out" });
  }

  async function opened(path: string) {
    // In-memory API only: the demo link signed us in; continue as the real link would.
    router.history.push(path);
    autoTried.current = false;
    await readSession();
  }

  if (!wellFormed)
    return (
      <AuthFrame title="This invitation link is incomplete">
        <EmptyState
          variant="compact"
          align="start"
          icon={LinkIcon}
          title="Open the link from your email again"
          description="The address bar is missing part of the invitation. Copy the whole link from the invitation email, or ask for a new invitation."
          titleAs="h2"
          actions={
            <Link to="/login" className={buttonVariants({ variant: "secondary" })}>
              Go to sign in
            </Link>
          }
        />
      </AuthFrame>
    );

  if (session.state === "loading" || (accepting && !refusal))
    return (
      <AuthFrame title="Accept your invitation">
        <div role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner />
          {accepting ? "Accepting the invitation…" : "Checking your session…"}
        </div>
      </AuthFrame>
    );

  if (session.state === "out")
    return (
      <AuthFrame
        title="Accept your invitation"
        description="You've been invited to a company on CompanyOS. Confirm your email to join: we'll send you a one-time sign-in link."
        footer={
          <>
            Already have a password?{" "}
            <Link
              to="/login"
              search={{ redirect: here }}
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              Sign in with it
            </Link>{" "}
            and you'll come back here.
          </>
        }
      >
        {refusal ? (
          <Notice tone="danger" title={refusal.title} role="alert">
            {refusal.body}
          </Notice>
        ) : null}
        <MagicLinkForm
          api={api}
          askName
          callbackPath={`${here}&continue=1`}
          submitLabel="Email me a sign-in link"
          onOpened={(path) => void opened(path)}
        />
      </AuthFrame>
    );

  const { me } = session;
  return (
    <AuthFrame
      title="Accept your invitation"
      description={
        <>
          Signed in as <span className="font-medium text-foreground">{me.user.email}</span>.
        </>
      }
    >
      {refusal ? (
        <Notice tone="danger" icon={CircleAlertIcon} title={refusal.title} role="alert">
          {refusal.body}
        </Notice>
      ) : null}
      {refusal?.relink ? (
        <MagicLinkForm
          api={api}
          defaultEmail={me.user.email}
          callbackPath={`${here}&continue=1`}
          onOpened={(path) => void opened(path)}
        />
      ) : refusal?.open ? (
        <Link
          to="/c/$slug"
          params={{ slug: refusal.open.slug }}
          className={buttonVariants({ size: "lg" })}
        >
          Go to {refusal.open.name}
        </Link>
      ) : (
        <Button size="lg" onClick={() => void accept(me)}>
          Accept invitation
        </Button>
      )}
      <Button variant="ghost" className="self-start" onClick={() => void signOut()}>
        Not {me.user.email}? Use a different account
      </Button>
    </AuthFrame>
  );
}
